import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BrandMark } from "@/components/Nav";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
      toast.success("If this email exists, a reset link is on its way.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative" style={{ background: "var(--hs-bg)" }}>
      <div className="hs-noise" />
      <header className="p-8 flex items-center justify-between border-b border-hs">
        <Link to="/" className="flex items-center gap-3">
          <BrandMark />
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-hs pl-3 border-l border-hs">
            Development Review
          </span>
        </Link>
        <Link to="/login" className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs hs-link inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-md w-full"
        >
          <span className="hs-chip mb-8"><span className="dot" /> RECOVERY</span>
          <h1 className="font-display text-5xl leading-[0.95] mb-3">Reset password.</h1>
          <p className="text-muted-hs mb-12">Enter the email on your Harsight account. We'll email a secure reset link.</p>

          {done ? (
            <div className="border border-hs p-8" data-testid="forgot-success">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-warm mb-3">SENT</div>
              <p className="text-muted-hs">Check your inbox at <span className="text-white">{email}</span>. The link expires in one hour.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="forgot-form">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">Email</span>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="hs-input" data-testid="forgot-input-email" />
              </label>
              {error && (
                <div className="font-mono text-xs uppercase tracking-[0.15em] text-warm border border-warm/40 px-4 py-3">{error}</div>
              )}
              <button type="submit" disabled={loading} className="hs-btn hs-btn-primary w-full justify-center" data-testid="forgot-submit">
                {loading ? "Sending…" : "Send reset link"} <ArrowRight size={14} />
              </button>
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
}
